import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, Mail, Phone, Instagram } from "lucide-react";
import { trackEvent } from "@/lib/track";
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_HREF,
  INSTAGRAM_URL,
  OFFICE_ADDRESS,
  whatsappHref,
} from "@/lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = ["₹40 – 60 L", "₹60 L – 1 Cr", "₹1 – 2 Cr", "₹2 Cr+"];
const CHANNELS = ["Primary", "Resale", "Both"];
const TYPES = ["Office", "Retail", "Food Court", "Office Suite"];
const PURPOSES = ["Investment", "Own Use"];

const inputCls =
  "w-full bg-[#0B0C0E] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-[#F3F4F6] placeholder:text-[#4B5563] focus:outline-none focus:border-[#D4AF37]/60 focus:ring-1 focus:ring-[#D4AF37]/30 transition-all duration-300";

const EMPTY = {
  name: "",
  phone: "",
  budget: BUDGETS[0],
  channel: "Primary",
  unit_type: "Office",
  purpose: "Investment",
  visit_date: "",
};

export default function Enquiry() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const waMessage =
    `Hi Ashish, I'm contacting the Homewiz.in independent advisory desk to explore current EON Fairfox options.\n` +
    `Name: ${form.name || "—"}\nMobile: ${form.phone || "—"}\nBudget: ${form.budget}\n` +
    `Interested in: ${form.channel}\nLooking for: ${form.unit_type}\nPurpose: ${form.purpose}` +
    (form.visit_date ? `\nSite visit: ${form.visit_date}` : "");
  const waHref = whatsappHref(waMessage);

  const conciergeHref = whatsappHref(
    "Hi Ashish, I'm contacting the Homewiz.in independent advisory desk to explore current EON Fairfox options."
  );

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        phone: form.phone,
        unit_type: `${form.unit_type} · ${form.channel}`,
        budget: form.budget,
        visit_date: form.visit_date || null,
        message: `Purpose: ${form.purpose}`,
      });
      toast.success("Site visit requested", {
        description: "The Homewiz.in advisory team will reach out to confirm your slot.",
      });
      trackEvent("lead_form_submit", {
        channel: form.channel,
        unit_type: form.unit_type,
        budget: form.budget,
        purpose: form.purpose,
        has_visit_date: Boolean(form.visit_date),
      });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not submit request", {
        description: "Please check your details and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="visit" data-testid="enquiry-section" className="py-28 sm:py-36 bg-[#0D0F12]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-2 flex flex-col"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Independent EON Investment Desk</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6]">
            Explore the opportunity <span className="gold-text italic">first-hand.</span>
          </h2>
          <p className="mt-5 text-sm sm:text-base text-[#9CA3AF] leading-relaxed max-w-md">
            Request a guided visit and explore primary and resale opportunities with the Homewiz.in advisory team.
          </p>

          <a
            href={conciergeHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enquiry-whatsapp-btn"
            className="group mt-10 rounded-2xl border border-[#D4AF37]/30 bg-[#121418] p-6 flex items-center gap-5 hover:border-[#D4AF37] hover:shadow-[0_0_32px_rgba(212,175,55,0.15)] transition-all duration-500"
          >
            <div className="w-12 h-12 shrink-0 rounded-full bg-[#D4AF37] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <MessageCircle size={20} className="text-[#0B0C0E]" />
            </div>
            <div>
              <div className="font-sans font-semibold text-[#F3F4F6] text-sm">WhatsApp Ashish</div>
              <div className="text-xs text-[#9CA3AF] mt-1">Fastest way to get current primary &amp; resale options.</div>
            </div>
          </a>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=EON%20Fairfox%20Enquiry`}
              data-testid="enquiry-email-btn"
              className="rounded-2xl border border-white/10 bg-[#121418] px-5 py-4 flex items-center gap-3 hover:border-[#D4AF37]/50 transition-all duration-300"
            >
              <Mail size={16} className="text-[#D4AF37] shrink-0" />
              <span className="text-xs text-[#9CA3AF] break-all">{CONTACT_EMAIL}</span>
            </a>
            <a
              href={CONTACT_PHONE_HREF}
              data-testid="enquiry-call-btn"
              className="rounded-2xl border border-white/10 bg-[#121418] px-5 py-4 flex items-center gap-3 hover:border-[#D4AF37]/50 transition-all duration-300"
            >
              <Phone size={16} className="text-[#D4AF37] shrink-0" />
              <span className="text-xs text-[#9CA3AF]">{CONTACT_PHONE_DISPLAY}</span>
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enquiry-instagram-btn"
              className="rounded-2xl border border-white/10 bg-[#121418] px-5 py-4 flex items-center gap-3 hover:border-[#D4AF37]/50 transition-all duration-300"
            >
              <Instagram size={16} className="text-[#D4AF37] shrink-0" />
              <span className="text-xs text-[#9CA3AF]">@homewiz.in</span>
            </a>
          </div>

          <div className="mt-auto pt-10">
            <div data-testid="enquiry-office-address" className="border-t border-white/[0.07] pt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-[#6B7280] leading-loose">
              {OFFICE_ADDRESS.label}<br />
              <span className="text-[#9CA3AF]">{OFFICE_ADDRESS.line1}<br />{OFFICE_ADDRESS.line2}<br />{OFFICE_ADDRESS.line3}</span>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="site-visit-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-3 rounded-3xl border border-white/[0.07] bg-[#121418] p-8 sm:p-10"
        >
          <h3 className="font-display text-2xl font-semibold text-[#F3F4F6]">Get Current EON Options</h3>
          <p className="mt-2 text-xs text-[#9CA3AF]">Tell us what you're looking for — we'll help identify indicative primary and resale opportunities.</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mb-2">Name</label>
              <input data-testid="form-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mb-2">Mobile</label>
              <input data-testid="form-phone-input" type="tel" required value={form.phone} onChange={set("phone")} placeholder="+91 98XXX XXXXX" className={inputCls} />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mb-2">Budget</label>
              <select data-testid="form-budget-select" value={form.budget} onChange={set("budget")} className={`${inputCls} eon-select`}>
                {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mb-2">Primary / Resale</label>
              <select data-testid="form-channel-select" value={form.channel} onChange={set("channel")} className={`${inputCls} eon-select`}>
                {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mb-2">Office / Retail</label>
              <select data-testid="form-unit-type-select" value={form.unit_type} onChange={set("unit_type")} className={`${inputCls} eon-select`}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mb-2">Purpose</label>
              <select data-testid="form-purpose-select" value={form.purpose} onChange={set("purpose")} className={`${inputCls} eon-select`}>
                {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mb-2">Preferred Site Visit Date (optional)</label>
              <input data-testid="form-visit-date-input" type="date" value={form.visit_date} onChange={set("visit_date")} className={`${inputCls} [color-scheme:dark]`} />
            </div>
          </div>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="form-whatsapp-btn"
            className="mt-8 w-full py-4 rounded-full bg-[#D4AF37] text-[#0B0C0E] font-sans text-sm font-bold uppercase tracking-[0.2em] hover:bg-[#F3E5AB] hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] transition-all duration-300 flex items-center justify-center gap-3"
          >
            <MessageCircle size={15} />
            WhatsApp Current Options
          </a>
          <button
            type="submit"
            data-testid="form-submit-btn"
            disabled={submitting}
            className="mt-3 w-full py-4 rounded-full border border-[#D4AF37]/40 text-[#F3F4F6] font-sans text-xs font-semibold uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-3"
          >
            {submitting ? "Submitting…" : "Request Site Visit"}
            {!submitting && <Send size={14} className="text-[#D4AF37]" />}
          </button>
          <p data-testid="form-advisory-disclosure" className="mt-4 text-center text-[11px] text-[#6B7280]">
            By submitting your details, you agree to be contacted by Homewiz.in regarding this property and related real estate opportunities.
            <span className="block mt-1">Homewiz.in is an independent marketing and investment advisory desk and is not the official website of the project or developer.</span>
          </p>
        </motion.form>
      </div>
    </section>
  );
}
